// One message of an assistant thread (spec 2026-10-21-24 §12): a user
// message, an answer with its changes and its undo, or an undo report.
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import type { AssistantChange, AssistantMessage as Message } from '../../api/schema'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { runErrorText } from '../agent-runs/AgentRunCard'
import { AssistantKnowledgeProposalCard, proposalUidsIn } from '../knowledge/AssistantKnowledgeProposalCard'

export const SKIP_REASONS = ['changed_since', 'not_restorable', 'not_reversible', 'forbidden', 'already_deleted'] as const

/** isAppLink: only a relative link into the organization is clickable. */
export function isAppLink(href: string | undefined, org: string): boolean {
    return !!href && href.startsWith(`/${org}/`) && !href.startsWith('//')
}

/** changeHref is the page a change opens, or undefined when it has none. */
export function changeHref(org: string, change: AssistantChange): string | undefined {
    const link = change.link
    if (!link) return undefined
    const section: Record<string, string> = {
        asset: 'assets',
        contract: 'contracts',
        contact: 'users',
        intervention: 'interventions',
        expense: 'expenses',
        document: 'documents',
    }
    const s = section[link.type]
    return s ? `/${org}/${s}/${link.uid}` : undefined
}

function AppLink({ to, children, testId, className }: { to: string; children: ReactNode; testId?: string; className?: string }) {
    return (
        <Link to={to as '/'} className={className ?? 'text-primary underline'} data-testid={testId}>
            {children}
        </Link>
    )
}

interface UndoReport {
    answer_uid?: string
    reverted?: { entity_table: string; entity_uid: string; operation: string }[]
    skipped?: { entity_table: string; entity_uid: string; reason: string }[]
}

export interface AssistantMessageProps {
    org: string
    message: Message
    /** The run's error, for a turn that stopped. */
    runError?: string | null
    canOpenRun?: boolean
    onUndo?: (message: Message) => void
    undoBusy?: boolean
}

export function AssistantMessage({ org, message, runError, canOpenRun, onUndo, undoBusy }: AssistantMessageProps) {
    const { t } = useTranslation('assistant')
    const { t: tRuns } = useTranslation('agent-runs')
    const [confirm, setConfirm] = useState(false)

    if (message.role === 'system') {
        const report = message.context as UndoReport & { code?: string }
        if (report.code !== 'undo') return null
        const reverted = report.reverted ?? []
        const skipped = report.skipped ?? []
        return (
            <div
                className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground"
                data-testid={`assistant-undo-report-${report.answer_uid ?? message.uid}`}
            >
                <div data-testid="assistant-undo-report-count">{t('undo_report', { count: reverted.length })}</div>
                {skipped.length > 0 && (
                    <ul className="mt-1 list-disc pl-4">
                        {skipped.map((s) => (
                            <li key={s.entity_uid} data-testid={`assistant-undo-skipped-${s.entity_uid}`}>
                                {s.entity_table} · {t(`skip_reasons.${s.reason}`, { defaultValue: s.reason })}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        )
    }

    if (message.role === 'user') {
        return (
            <div className="flex flex-col items-end gap-1" data-testid={`assistant-message-${message.uid}`}>
                <div className="max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                    {message.text}
                </div>
                {message.turn_state === 'stopped' && (
                    <div className="text-xs text-destructive" data-testid={`assistant-stopped-${message.uid}`}>
                        {t('stopped')}
                        {runError ? ` · ${runErrorText(tRuns, runError)}` : ''}
                    </div>
                )}
            </div>
        )
    }

    const changes = message.changes ?? []
    const undone = !!message.undone_at
    return (
        <div className="flex flex-col gap-2" data-testid={`assistant-message-${message.uid}`}>
            <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        a: ({ href, children }) =>
                            isAppLink(href, org) ? (
                                <AppLink to={href as string} testId="assistant-message-link">
                                    {children}
                                </AppLink>
                            ) : (
                                <span data-testid="assistant-plain-link">{children}</span>
                            ),
                    }}
                >
                    {message.text}
                </Markdown>
            </div>
            {proposalUidsIn(message.text, org).map((uid) => (
                <AssistantKnowledgeProposalCard key={uid} org={org} uid={uid} />
            ))}
            {changes.length > 0 && (
                <div className="rounded-md border px-3 py-2" data-testid={`assistant-changes-${message.uid}`}>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">{t('changes')}</div>
                    <ul className="space-y-0.5 text-sm">
                        {changes.map((c) => {
                            const href = changeHref(org, c)
                            const label = c.link?.label || c.entity_table
                            const cls = undone ? 'line-through text-muted-foreground' : 'text-primary underline'
                            return (
                                <li key={`${c.entity_table}-${c.entity_uid}`}>
                                    {href ? (
                                        <AppLink to={href} testId={`assistant-change-${c.entity_uid}`} className={cls}>
                                            {label}
                                        </AppLink>
                                    ) : (
                                        <span data-testid={`assistant-change-${c.entity_uid}`} className={undone ? 'line-through' : ''}>
                                            {label}
                                        </span>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
            <div className="flex items-center gap-2">
                {message.can_undo && onUndo && (
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={undoBusy}
                            onClick={() => setConfirm(true)}
                            data-testid={`assistant-undo-${message.uid}`}
                        >
                            {t('undo')}
                        </Button>
                        <AlertDialog open={confirm} onOpenChange={setConfirm}>
                            <AlertDialogContent data-testid="assistant-undo-confirm-dialog">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>{t('undo_confirm_title')}</AlertDialogTitle>
                                    <AlertDialogDescription>{t('undo_confirm_description')}</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="assistant-undo-cancel">{t('cancel')}</AlertDialogCancel>
                                    <AlertDialogAction data-testid="assistant-undo-confirm" onClick={() => onUndo(message)}>
                                        {t('undo_confirm_action')}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </>
                )}
                {canOpenRun && message.agent_run_uid && (
                    <Link
                        to="/$org/agent-runs/$runUid"
                        params={{ org, runUid: message.agent_run_uid }}
                        className="text-xs text-muted-foreground hover:underline"
                        data-testid="assistant-run-link"
                    >
                        {t('details')}
                    </Link>
                )}
            </div>
        </div>
    )
}

/** AssistantLiveTurn is the partial text of the turn in progress. */
export function AssistantLiveTurn({ text, runStatus }: { text: string; runStatus: string }) {
    const { t } = useTranslation('assistant')
    return (
        <div className="flex flex-col gap-1 text-sm" data-testid="assistant-live-turn">
            {text && <div className="whitespace-pre-wrap text-muted-foreground" data-testid="assistant-live-text">{text}</div>}
            <div className="animate-pulse text-xs text-muted-foreground">
                {runStatus === 'queued' ? t('waiting') : t('working')}
            </div>
        </div>
    )
}
